import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext.tsx';
import './AdminUserTrades.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

interface Trade {
  id:             number;
  symbol:         string;
  exchange:       string;
  instrumentType: string;
  strategyName:   string;
  entryMode:      string;
  tradeType:      string;
  direction:      string;
  entryDate:      string;
  exitDate:       string;
  quantity:       number | null;
  entryPrice:     number | null;
  exitPrice:      number | null;
  capitalUsed:    number | null;
  endCapital:     number | null;
  pnlAmount:      number;
  pnlPercent:     number | null;
  notes:          string | null;
}

const fmtDate = (ds: string) => {
  if (!ds) return '—';
  const [y, m, d] = ds.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
};

const fmtNum = (v: number | null, prefix = '₹') =>
  v == null ? '—' : `${prefix}${Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtPct = (v: number | null) =>
  v == null ? '' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

const AdminUserTrades: React.FC = () => {
  const navigate  = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const location  = useLocation() as any;
  const { user, isAuthenticated, isLoading } = useAuth();

  const userName  = location.state?.name  || `User #${userId}`;
  const userEmail = location.state?.email || '';

  const isAdmin = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    return user.roles?.some((r: string) => r.toUpperCase().includes('ADMIN'));
  }, [isAuthenticated, user]);

  const [trades,  setTrades]  = useState<Trade[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAdmin) { toast.error('Access denied'); navigate('/'); }
  }, [isLoading, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin || !userId) return;
    const fetchTrades = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const res   = await fetch(`${API_BASE_URL}/admin/journal/users/${userId}/trades`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const json = await res.json();
        setTrades(json.data || []);
      } catch {
        toast.error('Failed to load trades');
      } finally {
        setLoading(false);
      }
    };
    fetchTrades();
  }, [isAdmin, userId]);

  // Summary stats
  const stats = useMemo(() => {
    if (!trades.length) return null;
    const totalPnl   = trades.reduce((s, t) => s + (t.pnlAmount || 0), 0);
    const wins       = trades.filter(t => t.pnlAmount > 0).length;
    const losses     = trades.filter(t => t.pnlAmount < 0).length;
    const winRate    = Math.round((wins / trades.length) * 100);
    return { totalPnl, wins, losses, winRate, total: trades.length };
  }, [trades]);

  if (isLoading) return <div className="main-content"><div className="aut-loading">Checking permissions...</div></div>;
  if (!isAdmin)  return null;

  return (
    <div className="main-content">
      {/* Header */}
      <div className="aut-header">
        <button className="back-btn" onClick={() => navigate('/admin/journals')}>
          <FaArrowLeft />
        </button>
        <div>
          <h2 className="aut-title">{userName}</h2>
          {userEmail && <div className="aut-subtitle">{userEmail}</div>}
        </div>
      </div>

      {/* Summary cards */}
      {stats && (
        <div className="aut-stats-row">
          <div className="aut-stat-card">
            <div className="aut-stat-value">{stats.total}</div>
            <div className="aut-stat-label">Total Trades</div>
          </div>
          <div className={`aut-stat-card ${stats.totalPnl >= 0 ? 'profit' : 'loss'}`}>
            <div className="aut-stat-value">
              {stats.totalPnl >= 0 ? '+' : '-'}₹{Math.abs(stats.totalPnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
            <div className="aut-stat-label">Total P&amp;L</div>
          </div>
          <div className="aut-stat-card">
            <div className="aut-stat-value aut-win">{stats.wins}</div>
            <div className="aut-stat-label">Winning Trades</div>
          </div>
          <div className="aut-stat-card">
            <div className="aut-stat-value aut-loss">{stats.losses}</div>
            <div className="aut-stat-label">Losing Trades</div>
          </div>
          <div className="aut-stat-card">
            <div className="aut-stat-value">{stats.winRate}%</div>
            <div className="aut-stat-label">Win Rate</div>
          </div>
        </div>
      )}

      {/* Trade list */}
      {loading ? (
        <div className="aut-loading">Loading trades...</div>
      ) : trades.length === 0 ? (
        <div className="aut-empty">No trades found for this user.</div>
      ) : (
        <div className="aut-table-wrap">
          <table className="aut-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Symbol</th>
                <th>Direction</th>
                <th>Strategy</th>
                <th>Entry</th>
                <th>Exit</th>
                <th>Qty</th>
                <th>Entry Price</th>
                <th>Exit Price</th>
                <th>P&amp;L</th>
                <th>Exit Date</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => {
                const isProfit = t.pnlAmount >= 0;
                return (
                  <tr key={t.id} className="aut-trade-row">
                    <td className="aut-idx">{i + 1}</td>
                    <td>
                      <div className="aut-symbol">{t.symbol}</div>
                      <div className="aut-exchange">{t.exchange}</div>
                    </td>
                    <td>
                      <span className={`aut-dir-badge ${t.direction?.toLowerCase()}`}>
                        {t.direction}
                      </span>
                    </td>
                    <td className="aut-strategy">{t.strategyName || '—'}</td>
                    <td className="aut-date-cell">{fmtDate(t.entryDate)}</td>
                    <td className="aut-date-cell">{fmtDate(t.exitDate)}</td>
                    <td className="aut-qty">{t.quantity ?? '—'}</td>
                    <td className="aut-price">
                      {t.entryMode === 'CAPITAL'
                        ? fmtNum(t.capitalUsed)
                        : fmtNum(t.entryPrice)}
                    </td>
                    <td className="aut-price">
                      {t.entryMode === 'CAPITAL'
                        ? fmtNum(t.endCapital)
                        : fmtNum(t.exitPrice)}
                    </td>
                    <td>
                      <div className={`aut-pnl ${isProfit ? 'profit' : 'loss'}`}>
                        {isProfit ? '+' : '-'}₹{Math.abs(t.pnlAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </div>
                      {t.pnlPercent != null && (
                        <div className={`aut-pct ${isProfit ? 'profit' : 'loss'}`}>
                          {fmtPct(t.pnlPercent)}
                        </div>
                      )}
                    </td>
                    <td className="aut-date-cell">{fmtDate(t.exitDate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUserTrades;
